import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";

/**
 * NotFound — replaces React Router's silent fall-through.
 *
 * A moody quote + a single back link. Same aurora-wash + grain as the rest
 * of the app, so a 404 doesn't feel like leaving the app — it feels like
 * walking into a quiet, empty room. Quote rotates per-mount so reload
 * surprises slightly without being chaotic.
 */
const QUOTES = [
    "this page took a longer walk than expected.",
    "somewhere between the words, this one went missing.",
    "we looked. we listened. it isn't here.",
    "a quiet hallway, no doors today.",
    "the feeling here, it seems, was never written.",
];

export default function NotFound() {
    const [quote, setQuote] = useState(QUOTES[0]);
    useEffect(() => {
        setQuote(QUOTES[Math.floor(Math.random() * QUOTES.length)]);
    }, []);

    return (
        <div className="relative min-h-screen w-full aurora-wash flex items-center justify-center px-6" data-testid="not-found-screen">
            <Helmet>
                <title>{`not found — MoodCard`}</title>
                <meta name="robots" content="noindex" />
            </Helmet>

            <div className="absolute inset-0 grain pointer-events-none" />

            <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                className="relative z-10 text-center max-w-sm"
            >
                {/* Eyebrow — keeps it readable as a 404 without being loud */}
                <p
                    className="font-sans text-[10px] uppercase tracking-[0.45em] text-app-3 mb-5"
                    data-testid="not-found-eyebrow"
                >
                    404 · nothing here
                </p>

                <motion.p
                    animate={{ opacity: [0.92, 1, 0.92] }}
                    transition={{ duration: 3.4, repeat: Infinity, ease: "easeInOut" }}
                    className="font-serif italic text-app text-2xl sm:text-3xl leading-snug mb-10"
                    data-testid="not-found-quote"
                >
                    "{quote}"
                </motion.p>

                <Link
                    to="/"
                    data-testid="not-found-home-link"
                    className="btn-press inline-flex items-center gap-2 font-sans text-[11px] uppercase tracking-[0.35em] text-amber-200/80 hover:text-amber-100 transition-colors"
                >
                    <span className="h-1 w-1 rounded-full" style={{ background: "#d4af6e" }} />
                    return home
                </Link>
            </motion.div>
        </div>
    );
}
